import { LoadingButton } from '@mui/lab'
import { Box, Button } from '@mui/material'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Appearance, loadStripe, StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js'
import React, { useState } from 'react'
import { formatCentsInEuros } from '../../domain/GuestAdoption/guestAdoptionDonation'
import {
  GuestAdoptionCheckoutAdopterDto,
  GuestAdoptionPaymentIntentDto,
} from '../../interactors/clients/GuestAdoptionsClient'
import { InfoBox } from '../common/InfoBox'
import { GuestAdoptionAmountRow, GuestAdoptionCard, GuestAdoptionTitle } from './GuestAdoptionLayout'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string)

interface Props {
  animalName: string
  adopter: GuestAdoptionCheckoutAdopterDto
  paymentIntent: GuestAdoptionPaymentIntentDto
  onPaid: () => void
  onBack: () => void
}

export const GuestAdoptionPaymentStep: React.FC<Props> = ({ animalName, adopter, paymentIntent, onPaid, onBack }) => {
  const appearance: Appearance = {
    theme: 'stripe',
  }

  const options: StripeElementsOptions = {
    clientSecret: paymentIntent.clientSecret,
    appearance,
  }

  return (
    <GuestAdoptionCard>
      <GuestAdoptionTitle>Paiement</GuestAdoptionTitle>
      <GuestAdoptionAmountRow
        label={<>Frais d&apos;adoption de {animalName} :</>}
        amount={formatCentsInEuros(paymentIntent.amountInCents)}
      />
      {paymentIntent.donationInCents > 0 && (
        <GuestAdoptionAmountRow
          label="Votre contribution à Petso :"
          amount={formatCentsInEuros(paymentIntent.donationInCents)}
        />
      )}
      <GuestAdoptionAmountRow label="Total :" amount={formatCentsInEuros(paymentIntent.totalInCents)} />
      {/* A new PaymentIntent is created each time the donation changes: Elements must restart with the last secret */}
      <Elements key={paymentIntent.clientSecret} options={options} stripe={stripePromise}>
        <GuestCheckoutForm
          adopter={adopter}
          totalInCents={paymentIntent.totalInCents}
          onPaid={onPaid}
          onBack={onBack}
        />
      </Elements>
    </GuestAdoptionCard>
  )
}

interface FormProps {
  adopter: GuestAdoptionCheckoutAdopterDto
  totalInCents: number
  onPaid: () => void
  onBack: () => void
}

// Counterpart of `CheckoutForm` for an adopter who is not connected: the payer comes from the checkout link
// instead of the connected account, and the adopter stays on the page whenever no redirection is required.
const GuestCheckoutForm: React.FC<FormProps> = ({ adopter, totalInCents, onPaid, onBack }) => {
  const stripe = useStripe()
  const elements = useElements()

  const [message, setMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsLoading(true)
    setMessage(null)

    const hasAddress = Boolean(adopter.address && adopter.postalCode && adopter.city)

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        // Only used by the payment methods which leave the page: the adopter comes back to his own link.
        return_url: `${window.location.origin}${window.location.pathname}`,
        receipt_email: adopter.email,
        payment_method_data: {
          billing_details: {
            name: `${adopter.firstName} ${adopter.lastName}`,
            email: adopter.email,
            ...(adopter.phoneNumber ? { phone: adopter.phoneNumber } : {}),
            ...(hasAddress
              ? {
                  address: {
                    line1: adopter.address as string,
                    postal_code: adopter.postalCode as string,
                    city: adopter.city as string,
                  },
                }
              : {}),
          },
        },
      },
    })

    if (error) {
      if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
        setMessage(error.message)
      } else {
        setMessage('Une erreur inattendue est survenue.')
      }
      setIsLoading(false)
      return
    }

    if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
      onPaid()
      return
    }

    setMessage('Votre paiement a échoué. Veuillez réessayer.')
    setIsLoading(false)
  }

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: 'tabs',
    business: {
      name: 'Petso SAS',
    },
  }

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        onLoadError={() => setMessage(`Le formulaire de paiement n'a pas pu être chargé. Veuillez réessayer.`)}
      />
      {message && <InfoBox sx={{ p: 1 }} messageType="error" content={message} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={isLoading || !stripe || !elements}
          sx={{ fontSize: '18px' }}
        >
          {`Payer ${formatCentsInEuros(totalInCents)}`}
        </LoadingButton>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          disabled={isLoading}
          sx={{ fontSize: '18px' }}
          onClick={onBack}
        >
          Modifier ma contribution
        </Button>
      </Box>
    </form>
  )
}
